import CustomSnackBar from "../components/shared/CustomSnackBar";
import GroupForm from "../components/groups/GroupForm";
import GroupsList from "../components/groups/GroupsList";
import LoadingState from "../components/shared/LoadingState";
import { Box, Typography } from "@mui/material";
import { Group as GroupType } from "../interfaces/IGroups";
import { useGroupsContext } from "../contexts/GroupsContext";
import { useMemo, useState } from "react";
import { useParticipantsContext } from "../contexts/ParticipantsContext";

export default function GroupsFeature() {
  const { groups, isLoading, handleGroupAdd, handleGroupUpdate, handleGroupDelete } =
    useGroupsContext();
  const { participants } = useParticipantsContext();

  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const editingGroup = editingGroupId ? groups.find((g) => g.id === editingGroupId) : null;

  const handleEditGroup = (groupId: string) => {
    setEditingGroupId(groupId);
    setShowForm(true);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleAddGroup = () => {
    setEditingGroupId(null);
    setShowForm(true);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingGroupId(null);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleSubmitGroup = async (
    groupData: Omit<GroupType, "userId" | "createdAt" | "updatedAt">,
  ) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    let result;

    if (editingGroupId) {
      result = await handleGroupUpdate(editingGroupId, groupData);
    } else {
      result = await handleGroupAdd(groupData);
    }

    if (result.success) {
      setShowForm(false);
      setEditingGroupId(null);
      setSuccessMessage(
        editingGroupId ? "Grupa została zaktualizowana." : "Grupa została utworzona.",
      );
    } else {
      setErrorMessage(result.error || "Wystąpił błąd podczas zapisywania grupy.");
    }

    return result;
  };

  const handleDeleteGroup = async (groupId: string) => {
    const result = await handleGroupDelete(groupId);
    if (result.success) {
      setSuccessMessage("Grupa została usunięta.");
    } else {
      setErrorMessage(result.error || "Wystąpił błąd podczas usuwania grupy.");
    }
    return result;
  };

  if (isLoading) {
    return <LoadingState message="Ładowanie grup..." />;
  }

  const sortedGroups = useMemo(() => {
    const copy = [...groups];
    copy.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pl", { sensitivity: "base" }),
    );
    return copy;
  }, [groups]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
      {/* Nagłówek sekcji */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
          Grupy zawodników
        </Typography>
      </Box>

      {showForm ? (
        <GroupForm
          initialData={editingGroup || undefined}
          allParticipants={participants}
          existingGroups={groups}
          onSubmit={handleSubmitGroup}
          onCancel={handleCancelForm}
        />
      ) : (
        <GroupsList
          groups={sortedGroups}
          participants={participants}
          onEdit={handleEditGroup}
          onDelete={handleDeleteGroup}
          onAdd={handleAddGroup}
        />
      )}

      {/* Snackbar z błędami */}
      <CustomSnackBar
        open={!!errorMessage}
        onClose={() => setErrorMessage(null)}
        type="error"
        title="Błąd"
        message={errorMessage || ""}
        autoHideDuration={6000}
      />

      {/* Snackbar z sukcesem */}
      <CustomSnackBar
        open={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        type="success"
        title="Sukces"
        message={successMessage || ""}
        autoHideDuration={3000}
      />
    </Box>
  );
}
